/**
 * PageEditorShell — Floating toolbar layout for the Page Editor.
 * Adapted from PrintStudioShell to match its design system:
 * frosted glass pills, right-side floating sidebar, rounded preview area.
 */

import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'

interface PageEditorShellProps {
  /** Label shown next to back button (page name) */
  backLabel?: ReactNode
  /** Center toolbar content (device toggle) */
  centerSlot: ReactNode
  /** Right toolbar actions (theme, preview, publish) */
  actionsSlot: ReactNode
  /** Main preview/editor content */
  children: ReactNode
  /** Sidebar panel (PageEditorSidebar or CardEditorPanel) */
  sidebar?: ReactNode
}

export function PageEditorShell({
  backLabel,
  centerSlot,
  actionsSlot,
  children,
  sidebar,
}: PageEditorShellProps) {
  const navigate = useNavigate()

  return (
    <div className="relative flex h-full w-full flex-col gap-2 p-2">
      {/* Header — frosted glass toolbar pills */}
      <div className="flex items-center gap-2">
        {/* Left: back + page name */}
        <div className="flex items-center shrink-0 rounded-full bg-white dark:bg-black/50 shadow-sm">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-8 w-8 shrink-0"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </Button>
        </div>
        {backLabel && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
            {backLabel}
          </div>
        )}

        {/* Center */}
        <div className="flex flex-1 items-center justify-center gap-3">
          <div className="rounded-full bg-white dark:bg-black/50 shadow-sm">
            {centerSlot}
          </div>
        </div>

        {/* Right: individual action buttons */}
        <div className="flex items-center gap-1.5 shrink-0 [&>*]:rounded-full">
          {actionsSlot}
        </div>
      </div>

      {/* Preview + Sidebar */}
      <div className="relative flex flex-1 min-h-0 gap-2">
        <div className="relative flex-1 overflow-hidden rounded-xl">
          <div
            className="h-full overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {children}
          </div>
        </div>
        {sidebar}
      </div>
    </div>
  )
}
